import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

// ──────────────────────────────────────────────
// Base fetch helper
// ──────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
  ) {
    super(`API ${status}: ${statusText}`)
    this.name = 'ApiError'
  }
}

const WS_TOKEN_KEY = 'ws_auth_token'

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) }
  // Only set Content-Type: application/json when a body is present
  if (options.body !== undefined && options.body !== null) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
  }
  // Inject auth token automatically from localStorage
  const token = localStorage.getItem(WS_TOKEN_KEY)
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`
  }
  const res = await fetch(path, {
    ...options,
    credentials: 'include',
    headers,
  })
  if (!res.ok) {
    // 401 SESSION_EXPIRED → clear token so the app redirects to login on next render
    if (res.status === 401) {
      localStorage.removeItem(WS_TOKEN_KEY)
      window.dispatchEvent(new CustomEvent('ws:session-expired'))
    }
    const body = await res.json().catch(() => undefined)
    const errBody = body as Record<string, unknown> | undefined
    const msg = String(errBody?.error || errBody?.message || res.statusText)
    throw new ApiError(res.status, msg, body)
  }
  return res.json()
}

// ──────────────────────────────────────────────
// Shared types
// ──────────────────────────────────────────────

export interface PaginatedMeta {
  total: number
  page: number
  limit: number
  pages: number
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginatedMeta
}

// Re-export for convenience in hook files
export { useQuery, useMutation, useQueryClient }
